// src/App.js
import React, { Suspense, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { trackPageView } from './utils/analytics';
import { HelmetProvider } from 'react-helmet-async';
import Layout from './components/shared/layout';
import InterviewLanding from './pages/interviews/InterviewLanding';
import ComingSoon from './pages/ComingSoon';
import Homepage from './pages/Homepage';
import HomeBaseViewer from './pages/homebase/HomeBaseViewer';
import BlogIndex from './pages/blog/BlogIndex';
import CategoryPage from './pages/blog/CategoryPage'
import BlogPost from './pages/blog/BlogPost'
import RestaurantsIndex from './pages/restaurants/RestaurantsIndex';
import ActivitiesIndex from './pages/activities/ActivitiesIndex';
import LodgingIndex     from './pages/lodging/LodgingIndex';
import ScrollToTop from './components/shared/ScrollToTop'
import RegionsGuide from './pages/regions/RegionsGuide';
import RegionDetail from './pages/regions/RegionDetail';
import AreaDetail from './pages/areas/AreaDetail';
import ItineraryPage from './pages/itinerary/ItineraryPage';
import ItineraryExamples from './pages/itinerary/ItineraryExamples';
import KauaiItinerary from './pages/KauaiItinerary';
import ItineraryPlaybookRoute from './pages/itinerary/ItineraryPlaybookRoute';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsConditions from './pages/legal/TermsConditions';
import ContactSupport from './pages/support/ContactSupport';
import About from './pages/About';
import GuidePage from './pages/guides/GuidePage';
import GuidesIndex from './pages/guides/GuidesIndex';
import WeatherVisualization from './pages/weather/WeatherVisualization';
import PhotoPlanner from './pages/photo/PhotoPlanner';
import SunriseSunsetPlanner from './pages/sunrise-sunset/SunriseSunsetPlanner';
import BeachConditionsIndex from './pages/beach-conditions/BeachConditionsIndex';
import BeachConditionsDetail from './pages/beach-conditions/BeachConditionsDetail';
import EmailSignaturePage from './pages/email-signature/EmailSignaturePage';
import IslandHostRoute from './components/routing/IslandHostRoute';
import IslandCapabilityRoute from './components/routing/IslandCapabilityRoute';

const BEACH_CONDITIONS_ACTIVITY_SLUGS = [
  'swimming',
  'wading',
  'walking-along-shore',
  'snorkeling',
  'wave-riding',
  'sunbathing',
];

// Lazy load Firebase-heavy components to reduce main bundle size
const InterviewWizard = React.lazy(() => import('./pages/interviews/InterviewWizard'));
const InterviewReview = React.lazy(() => import('./pages/interviews/InterviewReview'));
const InterviewSaved = React.lazy(() => import('./pages/interviews/InterviewSaved'));
const InterviewShare = React.lazy(() => import('./pages/interviews/InterviewShare'));
const ItinerarySaved = React.lazy(() => import('./pages/itinerary/ItinerarySaved'));
const ItineraryShare = React.lazy(() => import('./pages/itinerary/ItineraryShare'));
const ItineraryCalendar = React.lazy(() => import('./pages/itinerary/ItineraryCalendar'));
const RestaurantsList = React.lazy(() => import('./pages/restaurants/RestaurantsList'));
const ActivitiesList = React.lazy(() => import('./pages/activities/ActivitiesList'));
const LodgingList = React.lazy(() => import('./pages/lodging/LodgingList'));
const PhotoPlannerList = React.lazy(() => import('./pages/photo/PhotoPlannerList'));
const PhotoSpotProfile = React.lazy(() => import('./pages/photo/PhotoSpotProfile'));
const HomepagePlanningLab = process.env.REACT_APP_ENABLE_HOMEPAGE_LAB === 'true'
  ? React.lazy(() => import('./pages/HomepagePlanningLab'))
  : null;
const PlanningPhasePortalPage = React.lazy(() => import('./pages/PlanningPhasePortalPage'));

// Lazy load profile components that use Swiper (reduce main bundle size)
const RestaurantProfile = React.lazy(() => import('./pages/restaurants/RestaurantProfile'));
const ActivityProfile = React.lazy(() => import('./pages/activities/ActivityProfile'));
const LodgingProfile = React.lazy(() => import('./pages/lodging/LodgingProfile'));

// Lazy load deep research component
const DeepResearch = React.lazy(() => import('./pages/deep-research/DeepResearch'));

// Lazy load Facts component
const Facts = React.lazy(() => import('./pages/facts/Facts'));
// Lazy load quiz pages (generic paths)
const HomeBaseQuiz = React.lazy(() => import('./pages/quiz/HomeBaseQuiz'));
const HomeBaseQuizResult = React.lazy(() => import('./pages/quiz/HomeBaseQuizResult'));
const ChooseIslandQuiz = React.lazy(() => import('./pages/quiz/ChooseIslandQuiz'));
const ChooseIslandResult = React.lazy(() => import('./pages/quiz/ChooseIslandResult'));

// Analytics tracking component
function Analytics() {
  const location = useLocation();
  const hasTrackedInitialPageView = useRef(false);

  useEffect(() => {
    if (!hasTrackedInitialPageView.current) {
      hasTrackedInitialPageView.current = true;
      return;
    }

    trackPageView(window.location.pathname + window.location.search);
  }, [location.pathname, location.search]);

  return null;
}
// import CmsBootstrapTest from './components/dev/CmsBootstrapTest';
// import DevDashboard from './components/dev/DevDashboard';



function App() {
  const islandRoute = (element) => <IslandHostRoute>{element}</IslandHostRoute>;
  const islandCapabilityRoute = (capability, element, fallbackTo) => (
    islandRoute(
      <IslandCapabilityRoute capability={capability} fallbackTo={fallbackTo}>
        {element}
      </IslandCapabilityRoute>
    )
  );

  return (
    <HelmetProvider>
      <Router>
        <Analytics />
        <ScrollToTop />
        <Routes>
          <Route path="/email-signature/*" element={<EmailSignaturePage />} />
          {/* Interview pages - No Layout */}
          <Route path="/interview/wizard" element={
            islandCapabilityRoute(
              'tripPlanner',
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading interview...</div>}>
                <InterviewWizard />
              </Suspense>
            )
          } />
          <Route path="/interview/edit/:firestoreId/step/:stepIndex" element={
            islandCapabilityRoute(
              'tripPlanner',
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading interview...</div>}>
                <InterviewWizard />
              </Suspense>
            )
          } />
          <Route path="/interview/review/:firestoreId" element={
            islandCapabilityRoute(
              'tripPlanner',
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading interview...</div>}>
                <InterviewReview />
              </Suspense>
            )
          } />
          <Route path="/homebase/:interviewId" element={islandCapabilityRoute('tripPlanner', <HomeBaseViewer />)} />
          <Route path="/itinerary/:itineraryId" element={islandCapabilityRoute('tripPlanner', <ItineraryPage />)} />
          <Route path="/itinerary/:itineraryId/:versionId" element={islandCapabilityRoute('tripPlanner', <ItineraryPage />)} />
          {/* All other routes - With Layout */}
          <Route path="/*" element={
            <Layout>
              <Routes>
                <Route path="/" element={<Homepage />} />
                {HomepagePlanningLab ? (
                  <Route
                    path="/homepage-planning-lab"
                    element={islandRoute(
                      <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center">Loading...</div>}>
                        <HomepagePlanningLab />
                      </Suspense>,
                    )}
                  />
                ) : null}
                <Route
                  path="/trip-planning/:phaseSlug"
                  element={islandRoute(
                    <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center">Loading...</div>}>
                      <PlanningPhasePortalPage />
                    </Suspense>,
                  )}
                />
                <Route path="/coming-soon" element={<ComingSoon />} />
                <Route path="/about" element={<About />} />
                <Route path="/quiz/choose-island" element={
                  <Suspense fallback={<div className="p-8 text-center">Loading quiz...</div>}>
                    <ChooseIslandQuiz />
                  </Suspense>
                } />
                <Route path="/quiz/choose-island/result/:choiceId" element={
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading results...</div>}>
                    <ChooseIslandResult />
                  </Suspense>
                } />
                <Route path="/kauai-itinerary" element={islandCapabilityRoute('tripPlanner', <KauaiItinerary />)} />
                <Route path="/kauai-itinerary/*" element={islandCapabilityRoute('tripPlanner', <ItineraryPlaybookRoute />)} />
                <Route path="/interview" element={islandCapabilityRoute('tripPlanner', <InterviewLanding />)} />
                <Route path="/blog" element={islandRoute(<BlogIndex />)} />
                <Route path="/blog/category/:slug" element={islandRoute(<CategoryPage />)} />
                <Route path="/blog/:slug" element={islandRoute(<BlogPost />)} />
                <Route path="/blog/:slug/deep-research" element={
                  islandRoute(
                    <Suspense fallback={<div className="p-8 text-center">Loading...</div>}>
                      <DeepResearch type="blogPost" />
                    </Suspense>
                  )
                } />
                <Route path="/restaurants" element={islandRoute(<RestaurantsIndex />)} />
                <Route path="/restaurants/list" element={
                  islandRoute(
                    <Suspense fallback={<div className="p-8 text-center">Loading restaurants...</div>}>
                      <RestaurantsList />
                    </Suspense>
                  )
                } />
                <Route path="/restaurants/:slug" element={
                  islandRoute(
                    <Suspense fallback={<div className="p-8 text-center">Loading...</div>}>
                      <RestaurantProfile />
                    </Suspense>
                  )
                } />
                <Route path="/restaurants/:slug/deep-research" element={
                  islandRoute(
                    <Suspense fallback={<div className="p-8 text-center">Loading...</div>}>
                      <DeepResearch type="restaurant" />
                    </Suspense>
                  )
                } />
                <Route path="/lodging" element={islandRoute(<LodgingIndex />)} />
                <Route path="/lodging/:slug" element={
                  islandRoute(
                    <Suspense fallback={<div className="p-8 text-center">Loading...</div>}>
                      <LodgingProfile />
                    </Suspense>
                  )
                } />
                <Route path="/lodging/:slug/deep-research" element={
                  islandRoute(
                    <Suspense fallback={<div className="p-8 text-center">Loading...</div>}>
                      <DeepResearch type="lodging" />
                    </Suspense>
                  )
                } />
                <Route path="/lodging/list" element={
                  islandRoute(
                    <Suspense fallback={<div className="p-8 text-center">Loading lodging...</div>}>
                      <LodgingList />
                    </Suspense>
                  )
                } />
                <Route
                  path="/featured"
                  element={islandCapabilityRoute(
                    'featuredItineraries',
                    <ItineraryExamples />,
                    '/coming-soon?feature=featured-itineraries',
                  )}
                />
                <Route path="/activities" element={islandRoute(<ActivitiesIndex />)} />
                <Route path="/activities/:slug" element={
                  islandRoute(
                    <Suspense fallback={<div className="p-8 text-center">Loading...</div>}>
                      <ActivityProfile />
                    </Suspense>
                  )
                } />
                <Route path="/activities/:slug/deep-research" element={
                  islandRoute(
                    <Suspense fallback={<div className="p-8 text-center">Loading...</div>}>
                      <DeepResearch type="activity" />
                    </Suspense>
                  )
                } />
                <Route path="/activities/list" element={
                  islandRoute(
                    <Suspense fallback={<div className="p-8 text-center">Loading activities...</div>}>
                      <ActivitiesList />
                    </Suspense>
                  )
                } />
                <Route path="/beach-conditions" element={islandCapabilityRoute('beachConditions', <BeachConditionsIndex />)} />
                {BEACH_CONDITIONS_ACTIVITY_SLUGS.map((activitySlug) => (
                  <Route
                    key={activitySlug}
                    path={`/beach-conditions/${activitySlug}`}
                    element={islandCapabilityRoute('beachConditions', <BeachConditionsIndex activitySlug={activitySlug} />)}
                  />
                ))}
                <Route path="/beach-conditions/:slug" element={islandCapabilityRoute('beachConditions', <BeachConditionsDetail />)} />
                <Route path="/interview/saved/:firestoreId" element={
                  islandCapabilityRoute(
                    'tripPlanner',
                    <Suspense fallback={<div className="p-8 text-center">Loading interview...</div>}>
                      <InterviewSaved />
                    </Suspense>
                  )
                } />
                <Route path="/interview/share/:firestoreId" element={
                  islandCapabilityRoute(
                    'tripPlanner',
                    <Suspense fallback={<div className="p-8 text-center">Loading interview...</div>}>
                      <InterviewShare />
                    </Suspense>
                  )
                } />
                <Route path="/itinerary/saved/:itineraryId/:versionId" element={
                  islandCapabilityRoute(
                    'tripPlanner',
                    <Suspense fallback={<div className="p-8 text-center">Loading itinerary...</div>}>
                      <ItinerarySaved />
                    </Suspense>
                  )
                } />
                <Route path="/itinerary/share/:itineraryId/:versionId" element={
                  islandCapabilityRoute(
                    'tripPlanner',
                    <Suspense fallback={<div className="p-8 text-center">Loading itinerary...</div>}>
                      <ItineraryShare />
                    </Suspense>
                  )
                } />
                <Route path="/itinerary/calendar/:itineraryId/:versionId" element={
                  islandCapabilityRoute(
                    'tripPlanner',
                    <Suspense fallback={<div className="p-8 text-center">Loading itinerary...</div>}>
                      <ItineraryCalendar />
                    </Suspense>
                  )
                } />
                {/* Back-compat: old preview route handled at top-level */}
                <Route path="/regions" element={islandRoute(<RegionsGuide />)} />
                <Route path="/regions/:slug" element={islandRoute(<RegionDetail />)} />
                <Route path="/areas/:slug" element={islandRoute(<AreaDetail />)} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsConditions />} />
                <Route path="/support" element={<ContactSupport />} />
                <Route path="/facts" element={
                  islandRoute(
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading facts...</div>}>
                      <Facts />
                    </Suspense>
                  )
                } />
                <Route path="/facts/:slug" element={
                  islandRoute(
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading facts...</div>}>
                      <Facts />
                    </Suspense>
                  )
                } />
                <Route path="/guides" element={islandRoute(<GuidesIndex />)} />
                <Route path="/guides/:slug" element={islandRoute(<GuidePage />)} />
                <Route path="/weather" element={islandCapabilityRoute('weatherComparison', <WeatherVisualization />)} />
                <Route path="/sunrise-sunset" element={islandCapabilityRoute('sunriseSunset', <SunriseSunsetPlanner />)} />
                <Route
                  path="/photo-planner"
                  element={islandCapabilityRoute('photoPlanner', <PhotoPlanner />, '/coming-soon?feature=photo-planner')}
                />
                <Route path="/photo-planner/:slug" element={
                  islandCapabilityRoute(
                    'photoPlanner',
                    <Suspense fallback={<div className="p-8 text-center">Loading photo spot...</div>}>
                      <PhotoSpotProfile />
                    </Suspense>,
                    '/coming-soon?feature=photo-planner',
                  )
                } />
                <Route path="/photo-planner/spots/:slug" element={
                  islandCapabilityRoute(
                    'photoPlanner',
                    <Suspense fallback={<div className="p-8 text-center">Loading photo spot...</div>}>
                      <PhotoSpotProfile />
                    </Suspense>,
                    '/coming-soon?feature=photo-planner',
                  )
                } />
                <Route path="/photo-planner/list" element={
                  islandCapabilityRoute(
                    'photoPlanner',
                    <Suspense fallback={<div className="p-8 text-center">Loading photo spots...</div>}>
                      <PhotoPlannerList />
                    </Suspense>,
                    '/coming-soon?feature=photo-planner',
                  )
                } />
                <Route path="/quiz/home-base" element={
                  islandCapabilityRoute(
                    'stayQuiz',
                    <Suspense fallback={<div className="p-8 text-center">Loading quiz...</div>}>
                      <HomeBaseQuiz />
                    </Suspense>
                  )
                } />
                {/* Quiz results page (canonical) */}
                <Route path="/quiz/home-base/result/:interviewId" element={
                  islandCapabilityRoute(
                    'stayQuiz',
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading results...</div>}>
                      <HomeBaseQuizResult />
                    </Suspense>
                  )
                } />
                {/* Back-compat for any older links */}
                <Route path="/quiz/home-base/result-codex/:interviewId" element={
                  islandCapabilityRoute(
                    'stayQuiz',
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading results...</div>}>
                      <HomeBaseQuizResult />
                    </Suspense>
                  )
                } />
                {/* Claude Quiz removed */}
              </Routes>
            </Layout>
          } />
        </Routes>
        {/* Temporary CMS Bootstrap Tests - Commented out after successful testing */}
        {/* <CmsBootstrapTest /> */}
        {/* <DevDashboard /> */}
      </Router>
    </HelmetProvider>
  )
}

export default App;
